import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Link2, Copy, RefreshCw, XCircle, Maximize2, X } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { toast } from "sonner";

function FullScreenQr({ url, ministryName, onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return createPortal(
    <div className="fixed inset-0 z-[100] bg-white flex flex-col items-center justify-center gap-8 p-8 animate-in fade-in" data-testid="join-qr-fullscreen">
      <button onClick={onClose} className="absolute top-6 right-6 p-2 rounded-full hover:bg-black/5" aria-label="Close" data-testid="join-qr-close-button"><X className="h-6 w-6" /></button>
      <p className="font-sacred text-4xl sm:text-5xl text-[#1E2A38] text-center">Join {ministryName}</p>
      <QRCodeSVG value={url} size={Math.min(window.innerHeight * 0.55, 560)} level="M" marginSize={2} data-testid="join-qr-large" />
      <p className="text-lg text-muted-foreground text-center">Scan with your phone camera to join</p>
    </div>,
    document.body
  );
}

export function JoinLinkPanel({ ministryId, ministryName }) {
  const [fullScreen, setFullScreen] = useState(false);
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);
  const base = `/orgs/ministries/${ministryId}/join-link`;
  const url = link ? `${window.location.origin}/join/${link.token}` : "";

  useEffect(() => {
    setLink(null);
    api.get(base).then(({ data }) => setLink(data)).catch(() => {});
  }, [base]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(base);
      setLink(data);
      toast.success("New invite link ready (old link no longer works)");
    } finally {
      setBusy(false);
    }
  };

  const revoke = async () => {
    await api.delete(base);
    setLink(null);
    toast.success("Invite link turned off");
  };

  const copy = async () => {
    await navigator.clipboard.writeText(url);
    toast.success("Link copied");
  };

  return (
    <div className="pt-4 border-t border-[var(--brand-border)] dark:border-[var(--brand-border-dark)] space-y-3" data-testid="join-link-panel">
      <p className="text-sm font-medium flex items-center gap-2"><Link2 className="h-4 w-4" /> Invite link</p>
      <p className="text-xs text-muted-foreground">Anyone with this link can join as a Member using Google, Apple or email. It works for 7 days.</p>
      {link ? (
        <>
          <div className="flex gap-2">
            <Input readOnly value={url} onFocus={(e) => e.target.select()} data-testid="join-link-url" />
            <Button variant="outline" onClick={copy} data-testid="join-link-copy-button"><Copy className="h-4 w-4" /></Button>
          </div>
          <div className="flex items-center gap-4">
            <div className="p-2 bg-white rounded-md border border-[var(--brand-border)]"><QRCodeSVG value={url} size={112} level="M" data-testid="join-qr-small" /></div>
            <div className="space-y-2">
              <p className="text-xs text-muted-foreground">Newcomers can scan this with their phone camera.</p>
              <Button size="sm" variant="outline" onClick={() => setFullScreen(true)} data-testid="join-qr-fullscreen-button"><Maximize2 className="h-3.5 w-3.5 mr-1.5" /> Show full screen</Button>
            </div>
          </div>
          {fullScreen && <FullScreenQr url={url} ministryName={ministryName} onClose={() => setFullScreen(false)} />}
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span data-testid="join-link-expiry">Expires {new Date(link.expires_at).toLocaleString()}</span>
            <div className="flex gap-1">
              <Button size="sm" variant="ghost" onClick={create} disabled={busy} data-testid="join-link-reset-button"><RefreshCw className="h-3.5 w-3.5 mr-1" /> New link</Button>
              <Button size="sm" variant="ghost" className="text-[var(--brand-crimson)]" onClick={revoke} data-testid="join-link-revoke-button"><XCircle className="h-3.5 w-3.5 mr-1" /> Turn off</Button>
            </div>
          </div>
        </>
      ) : (
        <Button variant="outline" onClick={create} disabled={busy} data-testid="join-link-create-button">
          <Link2 className="h-4 w-4 mr-2" /> {busy ? "Creating..." : "Create invite link"}
        </Button>
      )}
    </div>
  );
}
